"use client";

import { useEffect } from "react";
import { onCLS, onINP, onLCP, onTTFB, onFCP, type Metric } from "web-vitals";
import { track } from "@/lib/directory-tracking";

// Wrapper cliente que reporta Web Vitals y errores JS al helper de
// tracking. Se monta una vez en el root layout. Ver docs/tracking-plan.md.

// Rutas que no son páginas. Sin este filtro, `/_next/image` llegaba a
// ser la 2ª "página más vista" del dashboard con 130 sesiones — son
// requests de assets, no navegación.
function esRutaDePagina(path: string): boolean {
  if (!path) return false;
  return !(
    path.startsWith("/_next/") ||
    path.startsWith("/api/") ||
    /\.(png|jpe?g|webp|avif|gif|svg|ico|css|js|map|txt|xml|woff2?)$/i.test(path)
  );
}

// Muestreo de Web Vitals. Emiten 5 filas por pageview y llegaron a ser
// el 97 % de la tabla (2.100 de 2.169 eventos), tapando todo lo demás.
// Con `page_view` propio ya no se necesitan como proxy de tráfico:
// alcanza una fracción de las sesiones para vigilar performance.
const WEB_VITALS_SAMPLE_RATE = 0.2;
const VITALS_FLAG_KEY = "_dvitals";

function sesionMuestreada(): boolean {
  try {
    const cached = window.sessionStorage.getItem(VITALS_FLAG_KEY);
    if (cached !== null) return cached === "1";
    const inside = Math.random() < WEB_VITALS_SAMPLE_RATE;
    window.sessionStorage.setItem(VITALS_FLAG_KEY, inside ? "1" : "0");
    return inside;
  } catch {
    return false;
  }
}

// El navegador in-app de Instagram/Facebook inyecta sus propios scripts
// y genera errores que no son nuestros. Llegaron a ser 30 de los ~30
// `js_error` registrados, o sea el 100 % del ruido de errores.
const RUIDO_WEBVIEW = [
  "iabjs://",
  "navigation_performance_logger",
  "fbNavigationTiming",
  "instagram",
  // Variante iOS del webview de IG/FB: inyecta sendDataToNative inline
  // (el stack apunta a pedile.com/:1, sin "iabjs") y falla con
  // "window.webkit.messageHandlers" undefined fuera del webview real.
  "senddatatonative",
  "sendpagehidemessage",
  "webkit.messagehandlers",
];

function esRuidoDeWebView(message: string, stack: string | null): boolean {
  const t = `${message} ${stack ?? ""}`.toLowerCase();
  return RUIDO_WEBVIEW.some((p) => t.includes(p.toLowerCase()));
}

function reportMetric(metric: Metric): void {
  const path = typeof location !== "undefined" ? location.pathname : "";
  if (!esRutaDePagina(path)) return;
  track("web_vitals", {
    // dedupKey único por métrica × path — mismo browser dispara CLS
    // varias veces (final value cambia con la interacción); solo el
    // último cae por la ventana de dedup 5s.
    dedupKey: `${metric.name}:${path}`,
    metadata: {
      metric: metric.name,
      value: metric.value,
      rating: metric.rating,
      path,
    },
  });
}

function reportError(payload: {
  message: string;
  stack?: string | null;
  source?: string;
}): void {
  const msg = payload.message.slice(0, 300);
  const stack = payload.stack?.slice(0, 800) ?? null;
  if (esRuidoDeWebView(msg, stack)) return;
  const path = typeof location !== "undefined" ? location.pathname : "";
  track("js_error", {
    dedupKey: `${payload.source ?? "err"}:${msg}`,
    metadata: {
      message: msg,
      stack,
      source: payload.source ?? "window.onerror",
      path,
    },
  });
}

export default function HealthTracking(): null {
  useEffect(() => {
    // Web Vitals — se dispara cuando cada métrica está lista (CLS al
    // primer scroll estable, LCP al primer paint del hero, etc).
    // Solo en la fracción de sesiones muestreadas.
    if (sesionMuestreada()) {
      onCLS(reportMetric);
      onINP(reportMetric);
      onLCP(reportMetric);
      onTTFB(reportMetric);
      onFCP(reportMetric);
    }

    // JS error global. `window.onerror` captura errores sync. El
    // handler de `unhandledrejection` captura promises rechazadas.
    const onErr = (event: ErrorEvent) => {
      reportError({
        message: event.message || "unknown error",
        stack: event.error?.stack ?? null,
        source: "onerror",
      });
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === "string"
            ? reason
            : "unhandled promise rejection";
      const stack = reason instanceof Error ? reason.stack : null;
      reportError({ message, stack, source: "unhandledrejection" });
    };
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
